import { useEffect, useRef, useState } from "react";
import { getvideoGallery } from "../services/videoGallery.service";

export const useObsessionsinAction = () => {
  const sliderRef = useRef(null);
  const videoRefs = useRef([]);
  const reelVideoRefs = useRef([]);

  const [galleries, setGalleries] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [mutedIndex, setMutedIndex] = useState(null);

  // ✅ SAFE AUTOPLAY (browser compliant)
  const safePlay = async (video) => {
    if (!video) return;

    try {
      video.muted = true; // required for autoplay
      await video.play();
    } catch (err) {
      console.log("Play interrupted:", err?.name);
    }
  };

  // ✅ FETCH DATA
  useEffect(() => {
    const fetchData = async () => {
      const data = await getvideoGallery();
      setGalleries(data || []);
    };
    fetchData();
  }, []);

  const loopList = [...galleries, ...galleries];

  // ✅ INFINITE SCROLL
  const handleInfiniteScroll = () => {
    const slider = sliderRef.current;
    if (!slider) return;

    const halfWidth = slider.scrollWidth / 2;

    if (slider.scrollLeft >= halfWidth) {
      slider.scrollTo({ left: 1, behavior: "auto" });
    }

    if (slider.scrollLeft <= 0) {
      slider.scrollTo({ left: halfWidth - 1, behavior: "auto" });
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    slider.addEventListener("scroll", handleInfiniteScroll);
    return () => slider.removeEventListener("scroll", handleInfiniteScroll);
  }, []);

  // ✅ SCROLL BUTTON
  const scroll = (direction) => {
    if (!sliderRef.current) return;

    sliderRef.current.scrollBy({
      left: direction === "left" ? -320 : 320,
      behavior: "smooth",
    });

    // stop videos
    videoRefs.current.forEach((v) => {
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
    });

    setPlayingIndex(null);
  };

  // ✅ PLAY VIDEO
  const handlePlay = async (index, isReel = false) => {
    const refs = isReel ? reelVideoRefs.current : videoRefs.current;
    const video = refs[index];
    if (!video) return;

    // stop others
    refs.forEach((v, i) => {
      if (v && i !== index) {
        v.pause();
        v.currentTime = 0;
        v.muted = true;
      }
    });

    video.muted = true; // autoplay safe
    setPlayingIndex(index);

    await safePlay(video);
  };

  // ✅ PAUSE / RESUME
  const togglePause = async (index, isReel = false) => {
    const refs = isReel ? reelVideoRefs.current : videoRefs.current;
    const video = refs[index];
    if (!video) return;

    if (video.paused) {
      video.muted = false; // user interaction = sound ON
      setMutedIndex(index);
      setPlayingIndex(index);
      await safePlay(video);
    } else {
      video.pause();
      setPlayingIndex(null);
    }
  };

  // ✅ SOUND TOGGLE ONLY
  const toggleSound = (index, isReel = false) => {
    const refs = isReel ? reelVideoRefs.current : videoRefs.current;
    const video = refs[index];
    if (!video) return;

    video.muted = !video.muted;
    setMutedIndex(video.muted ? null : index);
  };

  // ✅ CLOSE REEL
  const closeReel = () => {
    setActiveIndex(null);
    setPlayingIndex(null);

    document.body.style.overflow = "auto";

    reelVideoRefs.current.forEach((v) => {
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
    });
  };

  // ✅ MOBILE STOP ON SCROLL
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    let timer;

    const handleScrollStop = () => {
      if (window.innerWidth > 768) return;

      clearTimeout(timer);
      timer = setTimeout(() => {
        videoRefs.current.forEach((video) => {
          if (video) {
            video.pause();
            video.currentTime = 0;
          }
        });

        setPlayingIndex(null);
      }, 150);
    };

    slider.addEventListener("scroll", handleScrollStop);

    return () => slider.removeEventListener("scroll", handleScrollStop);
  }, []);

  return {
    sliderRef,
    videoRefs,
    reelVideoRefs,
    galleries,
    loopList,
    activeIndex,
    playingIndex,
    mutedIndex,
    setActiveIndex,
    scroll,
    handlePlay,
    togglePause,
    toggleSound,
    closeReel,
  };
};